/**
 * §pilot-review — review-queue client. Two planes:
 *   master (Mission Control): list open review items across pilot tenants +
 *   resolve/dismiss (guarded server-side like the pilot lifecycle routes);
 *   tenant: flag one conversation / report a problem — both land as rows in
 *   the founder's queue and ping the "pilot" notification event.
 */
import { api } from "@/lib/api";

export type ReviewSource = "sample" | "flagged" | "tenant_flag" | "problem_report";
export type ReviewSeverity = "low" | "med" | "high";
export type ReviewStatus = "open" | "resolved" | "dismissed";
export type ReviewFailureKind =
  | "wrong_answer"
  | "refused_wrongly"
  | "tone"
  | "hallucination"
  | "process_break"
  | "other";

export interface ReviewItem {
  id: number;
  tenant_id: string;
  tenant_name: string;
  conversation_ref: string;
  channel: string;
  ts: number;
  source: ReviewSource;
  failure_kind: ReviewFailureKind | "";
  severity: ReviewSeverity;
  summary: string;
  evidence_quote: string;
  detail: Record<string, unknown>;
  status: ReviewStatus;
  resolved_note: string;
  resolved_by: string;
  resolved_at: number;
  created_at: number;
}

export interface ReviewCounts {
  open: number;
  high: number;
}

/** Open review items fleet-wide (severest first, then newest) + badge counts. */
export async function listReviewQueue(
  status: ReviewStatus | "all" = "open",
): Promise<{ items: ReviewItem[]; counts: ReviewCounts }> {
  return api<{ items: ReviewItem[]; counts: ReviewCounts }>(
    `/api/master/review?status=${encodeURIComponent(status)}`,
  );
}

/** Resolve or dismiss one open item (409 when someone else got there first). */
export async function decideReviewItem(
  id: number,
  status: "resolved" | "dismissed",
  note?: string,
): Promise<{ ok: boolean; item: ReviewItem }> {
  return api<{ ok: boolean; item: ReviewItem }>(
    `/api/master/review/${id}/decide`,
    { method: "POST", body: { status, note: note ?? "" } },
  );
}

/** Tenant-side: flag one conversation as wrong (creates a high-severity
 * queue item and notifies the founder). */
export async function flagConversation(
  conversationRef: string,
  reason: string,
  channel?: string,
): Promise<{ ok: boolean; id: number }> {
  return api<{ ok: boolean; id: number }>("/api/admin/review/flag", {
    method: "POST",
    body: { conversation_ref: conversationRef, reason, channel: channel ?? "" },
  });
}

export interface ProblemReportPayload {
  description: string;
  route: string;
  user_agent: string;
  console_errors: string[];
  conversation_refs: string[];
}

/** Tenant-side: the shell's "Report a problem" form. */
export async function reportProblem(
  payload: ProblemReportPayload,
): Promise<{ ok: boolean; id: number }> {
  return api<{ ok: boolean; id: number }>("/api/admin/problem-report", {
    method: "POST",
    body: payload,
  });
}
